import { ApiError } from '@rr-crm/shared/api-errors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch, throwIfNotOk } from './client';

/**
 * T1-014 — `useUserProfile()` hook for the CRM app.
 *
 * Wraps the three `/api/v1/me/profile/*` endpoints behind
 * react-query primitives. Profile data is cached indefinitely (no TTL)
 * since edits go through this hook's mutations and invalidate locally.
 *
 * Surfaces:
 *   - useUserProfile()                  — query: GET /me/profile (auto-creates server-side)
 *   - useUpdateUserProfile()            — mutation: PATCH /me/profile
 *   - useDismissSignaturePrompt()       — mutation: POST /me/profile/dismiss-signature-prompt
 *
 * Optimistic-update version handling: the mutations consume `expectedVersion`
 * from the cached profile. On 412 (version conflict), the API response
 * carries the current record; the hook updates the cache with that current
 * record so the user can re-attempt the edit against the fresh version.
 */

const QUERY_KEY = ['me', 'profile'] as const;

/**
 * RIG-238 — merge a profile mutation response into the cache WITHOUT dropping
 * `roles`. The PATCH / onboarding-state / dismiss-signature-prompt endpoints
 * return the updated user_profile row; `roles` is a JWT-derived field, not a
 * row column, so historically those responses omitted it. A blind
 * `setQueryData(data)` then wiped the cached `roles`, and the role-gated
 * Admin/Executive dashboards vanished from the nav after any profile save.
 *
 * The backend now echoes `roles` on these responses too (defense in depth);
 * this client-side merge guarantees a future role-less response can't regress
 * the nav. `next.roles` wins when present (authoritative, fresh from the JWT);
 * otherwise the previously cached roles are preserved.
 */
export function mergeProfilePreservingRoles(
  prev: UserProfile | undefined,
  next: UserProfile,
): UserProfile {
  if (!prev) return next;
  const roles = next.roles ?? prev.roles;
  // Conditional spread (not `roles: ...`) so we never assign an explicit
  // `undefined` — `exactOptionalPropertyTypes` forbids that for `roles?: string[]`.
  return { ...prev, ...next, ...(roles !== undefined ? { roles } : {}) };
}

export interface UserProfile {
  id: string;
  type: 'user_profile';
  schemaVersion: number;
  email: string;
  displayName?: string;
  preferredTimezone?: string;
  // T3-008B — sales rep phone number, US format (XXX) XXX-XXXX. Nullable.
  phoneNumber?: string | null;
  // T3-014A — sales rep job title (plain text 0-80 chars). Nullable.
  title?: string | null;
  // RIG-233 — sales-rep availability + derived current-book count.
  employmentType?: 'full_time' | 'part_time' | null;
  capacityTarget?: number | null;
  assignedActiveAccountCount?: number;
  emailSignatureHtml: string;
  // Overnight C6: the audit Cc address for this environment (empty = disabled).
  auditCcAddress?: string;
  emailSignatureUpdatedAt?: string;
  hasCompletedOnboarding: boolean;
  onboardingCompletedAt?: string;
  signaturePromptDismissedAt?: string;
  // Phase 10C — per-step onboarding progress (object of optional ISO timestamps).
  onboardingCompletionState?: {
    welcomeAt?: string;
    signatureAt?: string;
    tourAt?: string;
    firstActivityAt?: string;
    doneAt?: string;
  };
  createdAt: string;
  updatedAt: string;
  version: number;
  // T3-014J — caller's resolved Entra-group→role mapping. Empty array if
  // the user has no recognized staff role. Read-only on this endpoint.
  roles?: string[];
}

/**
 * T3-014J — small helper: is the user in an admin-like role?
 * Mirrors backend's `isAdminLike` in routes/accounts.ts. Used for UI
 * affordance gating (link visibility); the backend still enforces.
 */
export function isAdminRole(roles: string[] | undefined): boolean {
  if (!roles) return false;
  return roles.some((r) => r === 'superAdmin' || r === 'opsAdmin');
}

/**
 * RIG-655 — is the user a Super Admin? Mirrors the backend gate on
 * `POST /api/v1/submissions/:id/record-acceptance` (superAdmin only). UI
 * gating only — the backend gate remains the load-bearing control.
 */
export function isSuperAdmin(roles: string[] | undefined): boolean {
  return roles?.includes('superAdmin') ?? false;
}

/**
 * IA-005 — can the user CREATE an account? Mirrors the backend `STAFF_CREATORS`
 * gate on `POST /accounts` (superAdmin / opsAdmin / salesRep). The read-only
 * observer roles (viewAdmin, tech) 403 on submit, so the "New account"
 * affordances are dead-end CTAs for them and are hidden. UI gating only — the
 * backend gate remains the load-bearing control (SEE ≠ USE).
 */
export function isAccountCreator(roles: string[] | undefined): boolean {
  if (!roles) return false;
  return roles.some((r) => r === 'superAdmin' || r === 'opsAdmin' || r === 'salesRep');
}

/**
 * RIG-482 — can the user CHANGE an account? Mirrors the backend `STAFF_MUTATORS`
 * gate on `PATCH /accounts/:id` and `POST /accounts/:id/transition`.
 *
 * Same role set as `isAccountCreator`, named separately because it mirrors a
 * different backend constant: if one of the two gates ever moves, the other
 * must not move with it silently. UI gating only — the backend gate remains
 * the load-bearing control (SEE ≠ USE).
 */
export function canMutateAccount(roles: string[] | undefined): boolean {
  if (!roles) return false;
  return roles.some((r) => r === 'superAdmin' || r === 'opsAdmin' || r === 'salesRep');
}

export function useUserProfile() {
  return useQuery<UserProfile>({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const resp = await apiFetch('/api/v1/me/profile');
      await throwIfNotOk(resp);
      return resp.json() as Promise<UserProfile>;
    },
    staleTime: Infinity,
  });
}

export interface UpdateUserProfileInput {
  expectedVersion: number;
  displayName?: string;
  preferredTimezone?: string;
  // T3-008B — pass null to clear; pass a formatted "(XXX) XXX-XXXX" string to set.
  phoneNumber?: string | null;
  // T3-014A — pass null to clear; pass plain text (0-80 chars) to set.
  title?: string | null;
  // RIG-233 — sales-rep availability.
  employmentType?: 'full_time' | 'part_time' | null;
  capacityTarget?: number | null;
  emailSignatureHtml?: string;
}

export function useUpdateUserProfile() {
  const qc = useQueryClient();
  return useMutation<UserProfile, Error, UpdateUserProfileInput>({
    mutationFn: async (input) => {
      const resp = await apiFetch('/api/v1/me/profile', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (resp.status === 412) {
        // Version conflict — surface current record to the cache so the UI
        // can render the fresh version + present a merge UI.
        const conflict = (await resp.json()) as { error: string; current: UserProfile };
        qc.setQueryData<UserProfile>(QUERY_KEY, (prev) => mergeProfilePreservingRoles(prev, conflict.current));
        // QA 2026-10-01 F-28: an ApiError, so userMessage reads "Someone else changed this...".
        throw new ApiError({ status: 412, code: 'version_conflict', body: conflict });
      }
      await throwIfNotOk(resp);
      return resp.json() as Promise<UserProfile>;
    },
    onSuccess: (data) => {
      qc.setQueryData<UserProfile>(QUERY_KEY, (prev) => mergeProfilePreservingRoles(prev, data));
    },
  });
}

// Phase 10C — onboarding flow steps, mirror of the shared OnboardingStep enum.
export type OnboardingStep = 'welcome' | 'signature' | 'tour' | 'firstActivity' | 'done';

/**
 * Phase 10C — advance the calling user's onboarding step
 * (PATCH /me/profile/onboarding-state). Setting step='done' flips
 * hasCompletedOnboarding server-side. Consumes `expectedVersion` from the
 * cached profile; on 412 the conflict's current record refreshes the cache.
 */
export function useAdvanceOnboardingStep() {
  const qc = useQueryClient();
  return useMutation<UserProfile, Error, { expectedVersion: number; step: OnboardingStep; completed?: boolean }>({
    mutationFn: async (input) => {
      const resp = await apiFetch('/api/v1/me/profile/onboarding-state', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (resp.status === 412) {
        const conflict = (await resp.json()) as { error: string; current: UserProfile };
        qc.setQueryData<UserProfile>(QUERY_KEY, (prev) => mergeProfilePreservingRoles(prev, conflict.current));
        // QA 2026-10-01 F-28: an ApiError, so userMessage reads "Someone else changed this...".
        throw new ApiError({ status: 412, code: 'version_conflict', body: conflict });
      }
      await throwIfNotOk(resp);
      return resp.json() as Promise<UserProfile>;
    },
    onSuccess: (data) => {
      qc.setQueryData<UserProfile>(QUERY_KEY, (prev) => mergeProfilePreservingRoles(prev, data));
    },
  });
}

export function useDismissSignaturePrompt() {
  const qc = useQueryClient();
  return useMutation<UserProfile, Error, { expectedVersion: number }>({
    mutationFn: async (input) => {
      const resp = await apiFetch('/api/v1/me/profile/dismiss-signature-prompt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      if (resp.status === 412) {
        const conflict = (await resp.json()) as { error: string; current: UserProfile };
        qc.setQueryData<UserProfile>(QUERY_KEY, (prev) => mergeProfilePreservingRoles(prev, conflict.current));
        // QA 2026-10-01 F-28: an ApiError, so userMessage reads "Someone else changed this...".
        throw new ApiError({ status: 412, code: 'version_conflict', body: conflict });
      }
      await throwIfNotOk(resp);
      return resp.json() as Promise<UserProfile>;
    },
    onSuccess: (data) => {
      qc.setQueryData<UserProfile>(QUERY_KEY, (prev) => mergeProfilePreservingRoles(prev, data));
    },
  });
}
